type Listener = (...args: any[]) => void;

class EventBus {
  private events: { [key: string]: Listener[] } = {};
  // 用于存储延迟触发的事件（在监听器注册之前触发的事件）
  private pendingEvents: { [key: string]: any[][] } = {};

  on(event: string, listener: Listener): () => void {
    try {
      if (!this.events[event]) {
        this.events[event] = [];
      }
      this.events[event].push(listener);

      // 处理延迟的事件（如果有）
      if (this.pendingEvents[event] && this.pendingEvents[event].length > 0) {
        const pendingArgs = this.pendingEvents[event];
        // 清除待处理事件
        delete this.pendingEvents[event];
        // 立即触发这些事件
        pendingArgs.forEach(args => {
          try {
            listener(...args);
          } catch (error) {
            console.error(`[EventBus] 执行延迟事件监听器失败 (${event}):`, error);
          }
        });
      }

      // 返回一个取消订阅的函数
      return () => this.off(event, listener);
    } catch (error) {
      console.error(`[EventBus] 注册事件监听器失败 (${event}):`, error);
      // 返回一个空函数，避免调用时出错
      return () => {};
    }
  }

  off(event: string, listener: Listener): void {
    try {
      if (!this.events[event]) {
        return;
      }
      this.events[event] = this.events[event].filter(l => l !== listener);
    } catch (error) {
      console.error(`[EventBus] 移除事件监听器失败 (${event}):`, error);
    }
  }

  emit(event: string, ...args: any[]): void {
    try {
      // 检查是否有监听器
      if (!this.events[event] || this.events[event].length === 0) {
        // 如果没有监听器，将事件存储到待处理队列中（最多保留最近一次）
        // 这样可以处理组件懒加载的情况
        console.warn(`[EventBus] 事件 "${event}" 触发时没有监听器，事件将被延迟处理`);
        this.pendingEvents[event] = [args];
        return;
      }

      // 使用 try-catch 包裹每个监听器，确保一个监听器出错不会影响其他监听器
      this.events[event].forEach((listener, index) => {
        try {
          // 使用 Function.prototype.call 确保兼容性
          if (typeof listener === 'function') {
            listener(...args);
          } else {
            console.warn(`[EventBus] 事件 "${event}" 的监听器 ${index} 不是函数类型`);
          }
        } catch (error) {
          console.error(`[EventBus] 执行事件监听器失败 (${event}, index: ${index}):`, error);
        }
      });
    } catch (error) {
      console.error(`[EventBus] 触发事件失败 (${event}):`, error);
    }
  }

  // 清除所有事件监听器（用于测试或清理）
  clear(): void {
    this.events = {};
    this.pendingEvents = {};
  }

  // 获取某个事件的监听器数量（用于调试）
  getListenerCount(event: string): number {
    return this.events[event] ? this.events[event].length : 0;
  }
}

export const eventBus = new EventBus();
